import {
	Component,
	Host,
	h,
	Prop,
	Listen,
	Element,
	Watch,
	Event,
	EventEmitter ,
	State
} from "@stencil/core";

@Component({
	tag: "smart-plan-card",
	styleUrl: "smart-plan-card.scss",
	shadow: true,
})
export class SmartPlanCard {
	@Element() el: HTMLSmartPlanCardElement;
	private hiddenInput: HTMLInputElement;
	private inputName = "plan";

	@Prop() groupName?: string = "smart-plans";
	// caso true, exibi o input radio
	@Prop() showRadio?: boolean = false;
	// caso tenha link, o onClick vai ser no card inteiro
	@Prop() link?: string = "";

	@Prop({ mutable: true, reflect: true }) checked?: boolean = false;

	@Prop() bestOffer?: boolean = false;
	@Prop() bestOfferRegularText?: string;
	@Prop() bestOfferBoldText?: string;
	@Prop() promotionInstallmentsDisclaimer?: string;

	@Prop() planId!: string;
	@Prop() planLabel!: string;
	@Prop() planName!: string;
	@Prop() description: string;
	@Prop() offerDisclaimer: string;
	@Prop() oldPrice: string;
	@Prop() price: string;
	@Prop() priceRecurrency: string;
	@Prop() priceDiscount: string;
	@Prop() fidelity: string;
	@Prop() annuity: string;
	@Prop() membership: string;
	@Prop() disclaimer: string;
	@Prop({ reflect: true }) scarcityLabel: string;
	@Prop() collapseOpenLabel!: string;
	@Prop() collapseCloseLabel!: string;
	@Prop({ reflect: true }) hasCollapse = true;
	@Prop({ reflect: true }) shouldCollapseStartOpen = true;

	@Prop() theme: "light" | "dark" = "light";

	@Event() smartPlanChange: EventEmitter<string>;

	@Listen("collapse")
	collapseHandle(event: CustomEvent<boolean>) {
		return event.detail;
	}

	@State() itsChecked = false;

	@Listen("smartChange")
	smartChangeHandle() {
		this.updateValue();
	}

	@Watch("checked")
	watchCheckedHandler(newValue: boolean) {
		this.itsChecked = this.checked;
		if(this.hiddenInput) this.hiddenInput.checked = newValue;
		if(newValue === true) this.smartPlanChange.emit(this.planId);
	}

	private updateValue() {
		Array.from(document.querySelectorAll("smart-plan-card"))
			.filter((smartPlanCard) => smartPlanCard.getAttribute("data-group") === this.groupName)
			.forEach((smartPlanCard) => {
				smartPlanCard.checked = false;
			});

		if(this.hiddenInput) this.hiddenInput.checked = true;
		this.checked = true;
	}

	private handleSelectPlan() {
		if(this.showRadio && !this.link){
			this.updateValue();
		} else if(this.link) {
			window.location.href = this.link;
		}
	}

	private createHiddenInput() {
		this.hiddenInput = document.createElement("input");
		this.hiddenInput.type = "radio";
		this.hiddenInput.name = this.inputName;
		this.hiddenInput.value = this.planId;
		this.hiddenInput.style.visibility = "hidden";
		this.hiddenInput.style.position = "absolute";
		this.hiddenInput.style.pointerEvents = "none";
		this.el.appendChild(this.hiddenInput);
	}

	componentWillRender() {
		if(this.showRadio) this.createHiddenInput();
	}

	componentWillLoad() {
		if(this.showRadio) {
			this.itsChecked = this.checked;
		}
	}

	componentDidRender() {
		if(this.checked){
			this.el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
		}
	}

	private getList() {
		return (
			<smart-features-list
				theme={this.theme}
			>
				<slot></slot>
			</smart-features-list>
		);
	}

	render() {
		return (
			<Host
				part="plan-card-element"
				data-group={this.groupName}
				class={{
					"dark": this.theme === "dark",
					"light": this.theme === "light",
					"selected": this.checked,
					"isClickabe": !!this.link || this.showRadio
				}}
				onClick={() => this.handleSelectPlan()}>
				{this.bestOffer && this.bestOfferRegularText && this.bestOfferBoldText && (
					<smart-plan-badge
						class='best-offer-badge'
						regularText={this.bestOfferRegularText}
						boldText={this.bestOfferBoldText}>
					</smart-plan-badge>
				)}

				<div class='title-wrapper'>
					{this.showRadio && !this.link && (
						<smart-radio
							name='plan'
							value={this.planName}
							checked={this.itsChecked}
						>
						</smart-radio>
					)}
					<h2 class='title'>
						<smart-text class='plan-label' as='span' variant='headline-6'>
							{this.planLabel} {<b class='plan-name'>{this.planName}</b>}
						</smart-text>
					</h2>
				</div>

				{(this.description) && (
					<smart-text class='description' as='p' variant='bodytext-2' innerHTML={this.description}></smart-text>
				)}

				{this.scarcityLabel && (
					<span class='scarcity-badged'>
						{this.scarcityLabel}
					</span>
				)}

				{this.offerDisclaimer && (
					<smart-text class='offer-disclaimer' as='p' variant='bodytext-2'>
						{this.offerDisclaimer}
					</smart-text>
				)}
				{this.oldPrice && (
					<smart-text class='old-price' as='p' variant='bodytext-2'>
						{this.oldPrice}
					</smart-text>
				)}
				<p class='new-price'>
					<smart-text 
						class="new-price-amount" 
						as='span' 
						variant='headline-5' 
						innerHTML={`${this.price}${this.promotionInstallmentsDisclaimer ? "*" : ""}`}
					>
					</smart-text>
					{this.priceRecurrency && (		
						<smart-text 
							class="new-price-amount" 
							as='span' 
							variant='bodytext-2' 
						>
							{this.priceRecurrency}
						</smart-text>
					)}
					{this.priceDiscount && (		
						<smart-text 
							class="price-discount" 
							as='span' 
							variant='bodytext-2' 
						>
							{this.priceDiscount}
						</smart-text>
					)}
				</p>

				<div class="info-wrapper" part="plan-card-info">
					<smart-text 
						class="installments-disclaimer"
						as="p" 
						variant="bodytext-2"
						innerHTML={this.promotionInstallmentsDisclaimer}
					>
					</smart-text>

					<div class="info-wrapper__content">
						{this.fidelity && (
							<smart-text class='fidelity' as='p' variant='caption'>
								{this.fidelity}
							</smart-text>
						)}
						{this.membership && (
							<smart-text class='membership' as='p' variant='caption'>
								{this.membership}
							</smart-text>
						)}
						{this.annuity && (
							<smart-text class='annuity' as='p' variant='caption'>
								{this.annuity}
							</smart-text>
						)}
					</div>
				</div>

				<slot name="cta"></slot>

				{this.hasCollapse ? (
					<smart-collapse
						part="plan-card-content"
						class='content'
						open={this.shouldCollapseStartOpen}
						label={this.collapseOpenLabel}
						labelClosed={this.collapseCloseLabel}
						direction="up"
						iconColor={this.theme === "dark" ? "color-base-brand-white" : "color-action-default"}
						onCollapse={(event) => this.collapseHandle(event)}
					>
						{this.getList()}
					</smart-collapse>
				) : (
					<div class="card-content" part="plan-card-content">
						{this.getList()}
					</div>
				)}

				{this.disclaimer && (
					<smart-text class='disclaimer' as='p' variant='bodytext-2'>
						{this.disclaimer}
					</smart-text>
				)}
			</Host>
		);
	}
}
